
import WDElement from '../wd-element'
import WDNavbar from '../navbar/wd-navbar';

export default class WDPane extends WDElement
{
	#panes;
	#header;
	#body;
	#footer;
	#navbar = null;
	#filters = null;
	#toolbar = null;
	#backButton = null;
	#closeButton = null;
	#permissions = null;
	#baseSize = null;
	#currentSize = null;
	#appConfig = {
		title: null,
		transition: null,
		uri: null,
		route: null
	};

	constructor(options)
	{
		super().classList.add('wd-pane');

		this.#baseSize = options?.size || 100;
		this.#appConfig.title = options?.title || null;
		this.#appConfig.uri = options?.uri || null;
		this.#permissions = options?.permissions || null;

		this.dataset.baseSize = this.#baseSize;
	};

	connectedCallback()
	{
		super.connectedCallback();

		this.trigger('pane:added');
		this.panes.trigger('pane:added', { pane: this });

		//this allows me to know the index after the pane's removed from the DOM		
		const idx = this.panes.itemArray.indexOf(this);
		this.dataset.paneIndex = idx;

	};

	disconnectedCallback()
	{
		super.disconnectedCallback();

		this.trigger('pane:removed');
		this.panes.trigger('pane:removed', { pane: this });

		this.replaceChildren();
		this.offAll();

		this.#panes = null;
		this.#body = null;
		this.#footer = null;
		this.#navbar = null;
		this.#filters = null;
		this.#toolbar = null;
		this.#appConfig = null;
		this.#backButton = null;
		this.#closeButton = null;
		this.#permissions = null;
		this.#baseSize = null;
		this.#currentSize = null;
	};

	get body() 
	{
		if (!this.#body)
		{
			this.#body = wdc('<div class="wd-pane-body"/>');

			if (this.#header) this.#header.after(this.#body);
			else if (this.#footer) this.#footer.before(this.#body)
			else this.append(this.#body);
		}

		return this.#body;
	};

	get header() 
	{
		if (!this.#header)
		{
			this.#header = wdc('<div class="wd-pane-header"/>');
			this.prepend(this.#header);
		}

		return this.#header;
	};

	get footer() 
	{
		if (!this.#footer)
		{
			this.#footer = wdc('<div class="wd-pane-footer"/>');
			this.append(this.#footer);
		}

		return this.#footer;
	};

	get permissions() { return this.#permissions; };
	get realWidth()
	{
		return parseInt(this.panes.clientWidth * (this.size / 100));
	};

	updatePixelWidth(debug)
	{
		this.style.width = `${this.realWidth}px`;
	};

	updatePercentWidth()
	{
		this.style.width = `${this.size}%`;
	};

	get currentSize() { return this.#currentSize; };
	set currentSize(val)
	{
		this.#currentSize = val;
		this.setAttribute('data-current-size', val);
		this.dataset.currentSize = val;
	};

	get baseSize() { return this.#baseSize; };

	//read/write accessors	 
	get size()
	{
		if (this.panes.isSinglePane === true)
		{
			return 100;
		}
		else
		{
			return (this.#currentSize !== null) ? parseInt(this.#currentSize) : parseInt(this.#baseSize);
		}
	};

	set size(val) 
	{
		this.#currentSize = val;
		this.dataset.currentSize = val;
	};

	get visible() { return this.classList.contains('is-visible'); };
	set visible(val)
	{
		if (val === true) this.classList.add('is-visible');
		else this.classList.remove('is-visible');
	};

	get title() { return this.#appConfig?.title || ''; };
	set title(str)
	{
		this.#appConfig.title = str;
		this.trigger('title:updated', { pane: this, title: str });
	}

	get uri() { return this.#appConfig?.uri || ''; };
	set uri(str)
	{
		this.#appConfig.uri = str;
		this.trigger('title:updated', { pane: this, uri: str });
	}

	get appConfig() { return this.#appConfig; };
	set appConfig(cfg)
	{
		Object.assign(this.#appConfig, cfg);
	};

	set siteURI(uri) { this.uri = uri; };

	setSiteURI(uri)
	{
		this.uri(uri);
		//WDController.history().replace({uri: uri});
	};

	get panes()
	{
		if (!this.#panes) this.#panes = this.closest('.wd-panes');
		return this.#panes;
	};

	/**
		convenience functions
		*/

	pop(animated)
	{
		if (this.panes) this.panes.removePane(this, animated);
	};

	back() 
	{
		if (this.panes.tagName.toLowerCase() == 'wd-site-controller')
		{
			this.panes.back(this);
		}
		else
		{
			return this.pop(true);
		}
	};

	/**
		  pop to this pane by finding whatever pane is after this one
		  and popping it off the stack
		  */
	popTo(animated)
	{
		let nextPane = this.nextPane();
		if (nextPane) nextPane.pop(animated);
	};

	resetBody()
	{
		this.#body.empty();
	};

	resetToolbar()
	{
		this.#footer.empty();
		this.#toolbar = null;
	};

	resetNavbar()
	{
		if (this.#navbar) this.#navbar.remove();
		if (this.#backButton) this.#backButton = null;
		if (this.#closeButton) this.#closeButton = null;

		this.#navbar = null;
	};

	resetFilters()
	{
		if (this.#filters) this.#filters.remove();
		this.#filters = null;
	};

	empty()
	{
		this.#body.replaceChildren();

		this.resetNavbar();
		this.resetFilterbar();
		this.resetToolbar();
	};

	reset()
	{
		this.empty();
	};

	get index()
	{
		return (this.panes) ? this.panes.itemArray.indexOf(this) : -1;
	};

	get nextPane()
	{
		return (this.index < this.panes.items.length - 1) ? this.panes.items[this.index + 1] : null;
	};

	get prevPane()
	{
		return (this.index > 0) ? this.panes.items[this.index - 1] : null;
	};

	get isCurrent()
	{
		return this.isLastPane;
	};

	get isLastPane()
	{
		return (this.index == this.panes.items.length - 1);
	};

	addPane(p, animated)
	{
		if (animated === undefined) animated = true;
		this.panes.addAt(p, this.index + 1, animated);
	};

	/**
		build in navigation
		*/
	get navbar()
	{
		if (!this.#navbar)
		{
			this.#navbar = new WDNavbar();
			this.header.append(this.#navbar);
		}

		return this.#navbar;
	};

	get toolbar()
	{
		if (!this.#toolbar)
		{
			this.#toolbar = new WDNavbar({ collapsible: false });
			this.footer.append(this.#toolbar);
		}
		return this.#toolbar;
	};

	get hasFilters()
	{
		return this.#filters != null;
	};

	get filters() { return this.#filters; };
	set filters(cfg) { this.addFilters(cfg); };
	addFilters(cfg) { 
		this.#filters = this.navbar.addFilters(cfg); 
		return this.#filters;
	};
	
	/**
		  * back button on the left size of the navbar
		  */
	get backButton()
	{
		if (this.index === 0) return;

		if (!this.#backButton)
		{
			this.#backButton = this.navbar.prefix.add({ fasIcon: 'chevron-left' });
			this.#backButton.on('click', (evt) => this.back(evt));
			this.navbar.prefix.classList.add('has-back-button');
		}

		return this.#backButton;
	};

	set backButton(handler)
	{
		if ( typeof(handler) == 'function')
		{
			this.backButton.on('click', evt => {
				evt.preventDefault();
				evt.stopPropagation();

				handler();
			});
		}
		else
		{
			this.backButton;
		}
	};
	/**
		  * close button on the right side of the navbar
		  */
	get closeButton()
	{
		if (!this.#closeButton)
		{
			this.#closeButton = this.navbar.suffix.add({ fasIcon: 'times' });
			this.#closeButton.on('click', () => this.back());
		}

		return this.#closeButton;
	};

};

customElements.define('wd-pane', WDPane);
